import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { TicketIdLink } from '../TicketIdLink';
import { TicketHealthBadge } from './TicketHealthBadge';
import { useStaffCitations } from './StaffCitationsContext';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { formatSeconds, prettyFacet, valenceSwatch, valenceText } from '../../utils/staffProfile';
import { cn } from '../../utils/cn';

interface EvidenceChipProps {
  id: number;
  className?: string;
}

const POPOVER_WIDTH = 360;
const GAP = 8;

/**
 * Inline `#123` reference to one steward observation. Hover previews, click pins the
 * popover (Escape / outside click closes). Renders nothing when the id is unknown.
 */
export const EvidenceChip = memo(({ id, className }: EvidenceChipProps) => {
  const { citations, generatedAt } = useStaffCitations();
  const citation = citations[String(id)];
  const chipRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setPinned(false);
  }, []);

  useOnClickOutside(chipRef, () => {
    if (pinned) close();
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  useLayoutEffect(() => {
    if (!open || !chipRef.current) return;
    const rect = chipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(POPOVER_WIDTH, vw - 24);
    let left = Math.max(12, Math.min(rect.left, vw - width - 12));
    let top = rect.bottom + GAP;
    const estimatedHeight = popRef.current?.offsetHeight ?? 220;
    if (top + estimatedHeight > vh - 12 && rect.top - estimatedHeight - GAP > 12) {
      top = rect.top - estimatedHeight - GAP;
    }
    if (Number.isNaN(left)) left = 12;
    setPos({ top, left, width });
  }, [open]);

  useEffect(() => () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
  }, []);

  if (!citation) return null;

  const handleClick = (e: MouseEvent) => {
    // Chips live inside clickable rows/cards — never trigger those.
    e.stopPropagation();
    e.preventDefault();
    if (pinned) {
      close();
    } else {
      setPinned(true);
      setOpen(true);
    }
  };

  const handleEnter = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setOpen(true), 120);
  };

  const handleLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    if (!pinned) {
      hoverTimer.current = setTimeout(() => {
        if (!popRef.current?.matches(':hover')) setOpen(false);
      }, 200);
    }
  };

  const observedAgo = generatedAt ? `${formatSeconds(generatedAt - citation.observed_at)} ago` : null;
  const observedAbs = format(new Date(citation.observed_at * 1000), 'd MMM yyyy HH:mm');

  return (
    <>
      <button
        ref={chipRef}
        type="button"
        onClick={handleClick}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        aria-expanded={open}
        aria-label={`Evidence ${citation.id}`}
        className={cn(
          'inline-flex items-center h-5 font-mono text-[11px] leading-none px-1.5 rounded-md border align-middle transition-all',
          open
            ? 'border-accent-400 text-accent-300 bg-accent-500/15 shadow-[0_0_12px_rgb(var(--accent-t-500-rgb)/0.35)]'
            : 'border-glass-200/20 bg-glass-100/5 text-glass-400 hover:border-accent-400/60 hover:text-accent-300 hover:bg-accent-500/10',
          className,
        )}
      >
        #{citation.id}
      </button>
      {open && pos &&
        createPortal(
          <div
            ref={popRef}
            data-outside-click-ignore
            role="dialog"
            aria-label={`Evidence ${citation.id} detail`}
            onMouseEnter={() => {
              if (hoverTimer.current) clearTimeout(hoverTimer.current);
            }}
            onMouseLeave={handleLeave}
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 10000 }}
            className="glass-panel p-3.5 shadow-2xl shadow-black/60 text-sm border-accent-400/25"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-xs text-accent-300">#{citation.id}</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-glass-400">
                {prettyFacet(citation.facet)}
              </span>
              <span className={cn('ml-auto inline-flex items-center gap-1.5 text-xs font-mono', valenceText(citation.valence))}>
                <span className={cn('w-2 h-2 rounded-full', valenceSwatch(citation.valence))} aria-hidden="true" />
                {citation.valence}
              </span>
            </div>
            <p className="text-white text-[13px] leading-snug">{citation.fact}</p>
            {citation.quote && (
              <blockquote className="mt-2 px-2.5 py-1.5 border-l-2 border-accent-400 bg-glass-100/10 rounded-r text-xs text-glass-300 font-mono">
                “{citation.quote}”
              </blockquote>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-glass-400 font-mono">
              {citation.ticket_id && (
                <TicketIdLink ticketId={citation.ticket_id}>ticket #{citation.ticket_id}</TicketIdLink>
              )}
              <TicketHealthBadge health={citation.ticket_health} emptyLabel="open" className="text-[10px] py-0" />
              {observedAgo && <span>{observedAgo}</span>}
              <span title="Observed at">{observedAbs}</span>
              {citation.seen_count > 1 && <span>seen ×{citation.seen_count}</span>}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
});

EvidenceChip.displayName = 'EvidenceChip';
